/**
 * Model-written narrative has light structure: a bold line acting as a heading,
 * dash bullets, blank-line paragraph breaks. This turns that into blocks a
 * component can render as real elements.
 *
 * Why parse at all: the summaries genuinely contain "**What happened**" and
 * "- point", and both the old frontend and an unparsed render show the raw
 * asterisks to the user. That is a defect in the current product, not a design
 * choice, so it is fixed here rather than reproduced.
 *
 * Why blocks rather than HTML: the caller renders React elements, so nothing is
 * ever assembled into markup and there is no escaping to remember. Inline
 * emphasis is deliberately NOT supported — the value is structure, and every
 * inline rule is another chance to mangle a quote.
 */

export type RichBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }

const HEADING = /^\*\*(.+?)\*\*:?\s*$/
const HTML_HEADING = /^<(?:b|strong)>(.+?)<\/(?:b|strong)>:?\s*$/i
const BULLET = /^[-•*]\s+(.+)$/

/** Strip stray emphasis markers that survive as literal characters. */
function clean(line: string): string {
  return line.replace(/\*\*/g, '').replace(/<\/?(?:b|strong|i|em)>/gi, '').trim()
}

export function parseRichText(raw: string | null | undefined): RichBlock[] {
  if (!raw) return []
  const lines = String(raw).replace(/\r\n/g, '\n').split('\n')
  const blocks: RichBlock[] = []
  let paragraph: string[] = []
  let list: string[] = []

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') })
      paragraph = []
    }
  }
  const flushList = () => {
    if (list.length) {
      blocks.push({ kind: 'list', items: list })
      list = []
    }
  }

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) {
      flushList()
      flushParagraph()
      continue
    }
    const heading = trimmed.match(HEADING) ?? trimmed.match(HTML_HEADING)
    if (heading) {
      flushList()
      flushParagraph()
      blocks.push({ kind: 'heading', text: clean(heading[1] ?? '') })
      continue
    }
    const bullet = trimmed.match(BULLET)
    if (bullet) {
      flushParagraph()
      list.push(clean(bullet[1] ?? ''))
      continue
    }
    flushList()
    paragraph.push(clean(trimmed))
  }
  flushList()
  flushParagraph()
  return blocks.filter((b) => (b.kind === 'list' ? b.items.length > 0 : b.text.length > 0))
}

/**
 * Unstructured prose (a deal summary arriving as one block) still needs to be
 * readable. Group roughly three sentences per paragraph, honouring any breaks
 * the model already made.
 */
export function paragraphsFrom(text: string, perParagraph = 3): string[] {
  const trimmed = text.trim()
  if (!trimmed) return []
  if (/\n\s*\n/.test(trimmed)) return trimmed.split(/\n\s*\n/).map((p) => p.trim())
  const sentences = trimmed.split(/(?<=[.!?])\s+(?=[A-Z"'“(])/)
  const out: string[] = []
  let buffer: string[] = []
  for (const sentence of sentences) {
    const value = sentence.trim()
    if (value) buffer.push(value)
    if (buffer.length >= perParagraph) {
      out.push(buffer.join(' '))
      buffer = []
    }
  }
  if (buffer.length) out.push(buffer.join(' '))
  return out
}
